import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

const EN_TO_PT: Record<string, string> = {
  "/": "/pt",
  "/introduction": "/pt/introduction",
  "/environments": "/pt/environments",
  "/authentication": "/pt/authentication",
  "/errors": "/pt/errors",
  "/rate-limits": "/pt/rate-limits",
  "/portal/overview": "/pt/portal/overview",
  "/portal/quick-payment": "/pt/portal/quick-payment",
  "/portal/qr-payment": "/pt/portal/qr-payment",
  "/portal/references": "/pt/portal/references",
  "/portal/payment-links": "/pt/portal/payment-links",
  "/portal/payment-history": "/pt/portal/payment-history",
  "/portal/refunds": "/pt/portal/refunds",
  "/portal/finance": "/pt/portal/finance",
  "/portal/profile-settings-helpdesk":
    "/pt/portal/profile-settings-helpdesk",
  "/catalog": "/pt/catalog",
  "/legacy/payment-by-reference/information":
    "/pt/legacy/payment-by-reference/information",
  "/api/legacy/gpo": "/pt/api/legacy/gpo",
  "/api/legacy/payment-by-reference": "/pt/api/legacy/payment-by-reference",
  "/api": "/pt/api",
};

const PT_TO_EN = Object.fromEntries(
  Object.entries(EN_TO_PT).map(([en, pt]) => [pt, en]),
) as Record<string, string>;

const getTargetPath = (pathname: string, language: "en" | "pt") => {
  if (language === "pt") {
    if (pathname === "/pt") return "/";
    if (pathname.startsWith("/pt/api")) return pathname.replace("/pt/api", "/api");
    if (PT_TO_EN[pathname]) return PT_TO_EN[pathname];
    if (pathname.startsWith("/pt/")) return pathname.replace("/pt/", "/");
    return pathname;
  }

  if (pathname.startsWith("/api")) return pathname.replace("/api", "/pt/api");
  return EN_TO_PT[pathname] ?? `/pt${pathname}`;
};

export const LanguageToggle = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState<"en" | "pt">(
    location.pathname.startsWith("/pt") ? "pt" : "en",
  );

  useEffect(() => {
    const nextLanguage = location.pathname.startsWith("/pt") ? "pt" : "en";
    setLanguage(nextLanguage);
    document.documentElement.lang = nextLanguage === "pt" ? "pt-AO" : "en";
    localStorage.setItem("izi-pay-docs-language", nextLanguage);
  }, [location.pathname]);

  const nextLanguage = language === "pt" ? "en" : "pt";
  const label = useMemo(
    () =>
      language === "pt"
        ? "Switch documentation to English"
        : "Mudar documentação para português",
    [language],
  );

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => {
        const target = getTargetPath(location.pathname, language);
        navigate(`${target}${location.search}${location.hash}`);
      }}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border-0 p-0"
    >
      <span aria-hidden="true" className="flex items-center justify-center">
        {nextLanguage === "pt" ? (
          <img 
            src="/flags/ao.svg" 
            alt="Português" 
            className="h-6 w-6 rounded-full object-cover" 
            style={{ border: "none" }} 
          />
        ) : (
          <img 
            src="/flags/gb.svg" 
            alt="English" 
            className="h-6 w-6 rounded-full object-cover" 
            style={{ border: "none" }} 
          />
        )}
      </span>
    </button>
  );
};
